import { Trans, useTranslation } from 'react-i18next'
import { CircularProgressbarWithChildren } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import styles from './LicenceGraphs.module.css'
import i18next from 'i18next'
import { Tooltip } from '../../../../common/components/tooltip/Tooltip'
import { useBusinessSettings } from '../../../../contexts/BusinessSettingsContext'
import { useEffect } from 'react'
import { BusinessQuotaInfo } from '../../../../types/commonTypes'
import { dateTimeToDashDateString, parseDDMMYYYYcomma, safeIsNullOrEmpty } from '../../../../util/string'

type LicenceGraphsProps = {
  emailLicenseHolders: number
  pinLicenseHolders: number
  licenseValidUntilDate: string
  licenceEndDate: string
  isRenewing: boolean
}

export const getGraphTimes = (nextDateWhenLicencesFreed: string | undefined, licenceEndDate: string) => {
  if (!nextDateWhenLicencesFreed && !licenceEndDate) return { daysInRenewPeriod: -1, daysToRenew: -1 }
  const lastDate = parseDDMMYYYYcomma(nextDateWhenLicencesFreed || licenceEndDate)
  const now = new Date().getTime()
  const renewDate = new Date(Date.parse(lastDate))
  const renewTime = renewDate.getTime()
  let lastRenew = new Date(Date.parse(lastDate)).setMonth(renewDate.getMonth() - 3)

  const msInDay = 24 * 60 * 60 * 1000
  const daysToRenew = Math.floor((renewTime - now) / msInDay) + 1
  const daysInRenewPeriod = Math.floor((renewTime - lastRenew) / msInDay)

  return { daysInRenewPeriod, daysToRenew }
}

export const getLicenceErrors = (
  daysInRenewPeriod: number,
  daysToRenew: number,
  quota: BusinessQuotaInfo | undefined,
): React.ReactElement[] => {
  const shopLink = `https://play.seppo.io/newshop/shop?l=${i18next.language}`
  const returnable: React.ReactElement[] = []

  /* daysInRenewPeriod has been set (it is renewable account), but there is less than 1 day left */
  if (daysInRenewPeriod >= 0 && daysToRenew < 1)
    returnable.push(
      <div className={styles.licenceError} key={'renew-error'}>
        <Trans
          i18nKey='settings_organization.players.licence_progress.past_renew_date_description'
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          components={{ strong: <b />, 1: <a href={shopLink} target='_blank' rel='noopener noreferrer' /> }}
        >
          {
            '<strong>Your licence renew date has passed and you licence has not been renewed. To continue playing, buy more player licences from <1>Seppo’s shop</1>.</strong>'
          }
        </Trans>
      </div>,
    )
  if (quota?.freeSeats === 0) {
    returnable.push(
      <div className={styles.licenceError} key={'full-error'}>
        <Trans
          i18nKey='settings_organization.players.licence_progress.all_licences_used_description'
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          components={{ strong: <b />, 1: <a href={shopLink} target='_blank' rel='noopener noreferrer' /> }}
        >
          {
            '<strong>All your player licences are currently in use. If you need more before seats free up, you can purchase additional licences from <1>Seppo’s shop</1>.</strong>'
          }
        </Trans>
      </div>,
    )
  }
  return returnable
}

export const LicenceGraphs: React.FC<LicenceGraphsProps> = ({
  emailLicenseHolders,
  pinLicenseHolders,
  licenseValidUntilDate,
  isRenewing,
  licenceEndDate,
}) => {
  const { t } = useTranslation()
  const { updateQuota, quota } = useBusinessSettings()

  const licenceCircularProgressStyles = {
    root: {},
    path: {
      stroke: 'var(--primary-normal)',
    },
    trail: {
      stroke: 'var(--grey-100)',
    },
    text: {
      fill: 'var(--grey-700)',
      fontSize: '11px',
    },
  }

  const typeCircularProgressStyles = {
    root: {},
    path: {
      stroke: 'var(--primary-normal)',
    },
    trail: {
      stroke: 'color-mix(in srgb, var(--primary-normal) 46%, white)',
    },
    text: {
      fill: 'var(--grey-700)',
      fontSize: '11px',
    },
  }

  useEffect(() => {
    updateQuota()
    const interval = setInterval(() => {
      updateQuota()
    }, 30000)

    return () => clearInterval(interval)
  }, [updateQuota])

  const { daysInRenewPeriod, daysToRenew } = getGraphTimes(licenseValidUntilDate, licenceEndDate)
  const progressBarWidth = `${Math.max(100 - (daysToRenew / daysInRenewPeriod) * 100, 2)}%`
  const lastDate = licenseValidUntilDate || licenceEndDate

  return (
    <div className={styles.licenceGraphSectionScroll}>
      <div className={styles.licenceGraphSection}>
        <>
          <div className={styles.description}>
            {getLicenceErrors(daysInRenewPeriod, daysToRenew, quota).map((element) => element)}
          </div>
          {quota?.usedSeats === null || quota?.usedSeats === 0 ? (
            <p className={styles.noDataText}>
              {t(
                'settings_organization.players.licence_progress.no_players_text',
                'After players log in to your games, player licence usage statistics will be shown here.',
              )}
            </p>
          ) : (
            <div className={styles.circleProgressSection}>
              <div className={styles.circleProgressContainer}>
                <CircularProgressbarWithChildren
                  value={(quota?.totalQuotaUsage || 0) * 100}
                  styles={licenceCircularProgressStyles}
                  strokeWidth={10}
                >
                  <div className={styles.licenceCircleProgressMainText}>{quota?.usedSeats}</div>
                  <div className={styles.licenceCircleProgressSecondaryText}>
                    {t('settings_organization.players.licence_progress.secondary_text', {
                      max_players: quota?.totalSeats,
                      defaultValue: ' of %{max_players} licences used',
                    })}
                  </div>
                </CircularProgressbarWithChildren>
              </div>
              <div className={styles.circleProgressContainer}>
                <CircularProgressbarWithChildren
                  value={(emailLicenseHolders / (emailLicenseHolders + pinLicenseHolders)) * 100}
                  styles={typeCircularProgressStyles}
                  strokeWidth={10}
                >
                  <div className={styles.typeTextContainer}>
                    <Tooltip
                      tooltipContent={t('settings_organization.players.licence_type.tooltip.accounts', {
                        licences: emailLicenseHolders,
                        defaultValue: '%{licences} player accounts or email invites',
                      })}
                    >
                      {(tooltipProps) => (
                        <div className={styles.typeCircularInfoRow} {...tooltipProps}>
                          <div className={styles.firstColor} />
                          <div className={styles.typeCircleProgressSecondaryText}>
                            {t(
                              'settings_organization.players.licence_type.first_color',
                              'Player accounts and email invites',
                            )}
                          </div>
                        </div>
                      )}
                    </Tooltip>
                    <Tooltip
                      tooltipContent={t('settings_organization.players.licence_type.tooltip.players', {
                        licences: pinLicenseHolders,
                        defaultValue: '%{licences} game specific pin or QR code players',
                      })}
                    >
                      {(tooltipProps) => (
                        <div className={styles.typeCircularInfoRow} {...tooltipProps}>
                          <div className={styles.secondColor} />
                          <div className={styles.typeCircleProgressSecondaryText}>
                            {t(
                              'settings_organization.players.licence_type.second_color',
                              'Game specific pin or QR code',
                            )}
                          </div>
                        </div>
                      )}
                    </Tooltip>
                  </div>
                </CircularProgressbarWithChildren>
              </div>
            </div>
          )}
          {quota?.usedSeats && quota?.usedSeats !== 0 && daysInRenewPeriod >= 0 && (
            <div className={styles.progressBarSection}>
              {!safeIsNullOrEmpty(licenseValidUntilDate) && licenceEndDate !== licenseValidUntilDate ? (
                <div className={styles.renewedTitle}>
                  <Trans
                    i18nKey='settings_organization.players.renew_progress.title'
                    values={{
                      days: daysToRenew,
                      date: dateTimeToDashDateString(parseDDMMYYYYcomma(lastDate)),
                    }}
                    components={{ bold: <strong /> }}
                    defaults='Licenses renewed in <bold>{{days}}</bold> days, on <bold>{{date}}</bold>'
                  />
                </div>
              ) : (
                <div className={styles.renewedTitle}>
                  <Trans
                    i18nKey='settings_organization.players.licence_progress.title'
                    values={{
                      days: daysToRenew,
                      date: dateTimeToDashDateString(parseDDMMYYYYcomma(lastDate)),
                    }}
                    components={{ bold: <strong /> }}
                    defaults='Licences valid for <bold>{{days}}</bold> days, until <bold>{{date}}</bold>'
                  />
                </div>
              )}
              <div className={styles.progressBarBackground}>
                <div className={styles.progressBar} style={{ width: progressBarWidth }} />
              </div>
            </div>
          )}
        </>
      </div>
    </div>
  )
}
